import {
    API_ENDPOINTS,
    Card,
    CardBody,
    customToast,
    FormInput,
    route,
    Stepper,
    StringHelpers,
    SubTitle,
    useDisabledContext,
    useLangContext,
    Variants,
} from 'carrier-fe';
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ClaimsFormData, DistributorDetails } from '../../types/claim';
import { UseFormReturn } from '../../types/journey';
import { ClaimPartModule, DISTRIBUTOR_CLAIM_TYPES } from '../../util/Constants';

const MAX_TEXT_AREA_LENGTH = 255;

function ClaimsStep3({
    form,
}: {
    form: UseFormReturn<ClaimsFormData, any>;
}) {
    // Hooks and Contexts
    const { setValues, store, errors } = form;
    const { crud, fields } = useLangContext();
    const { disabled } = useDisabledContext();

    // State Management
    const [distributorDetails, setDistributorDetails] = useState<DistributorDetails>({
        distributor_claim_type: null,
        dispatch_note_field_label: null,
        dispatch_note_required: false,
    });
    const [loading, setLoading] = useState(true);

    // Effects

    // Fetch distributor details on mount to determine conditional field visibility
    useEffect(() => {
        if (!store?.distributor_id) {
            setLoading(false);
            return;
        }

        const fetchDistributorDetails = async () => {
            try {
                const response = await axios.get<{ data: DistributorDetails }>(
                    route(
                        API_ENDPOINTS.REGISTRATION.CLAIM.DISTRIBUTOR_CONFIG,
                        { distributorId: store.distributor_id }
                    )
                );

                const distributor = response.data?.data;

                if (distributor) {
                    setDistributorDetails({
                        distributor_claim_type: distributor.distributor_claim_type ?? null,
                        dispatch_note_field_label: distributor.dispatch_note_field_label ?? null,
                        dispatch_note_required: distributor.dispatch_note_required ?? false,
                    });
                }
            } catch (err: any) {
                customToast({
                    title: err?.response?.data?.message || 'Failed to load distributor details.',
                    variant: Variants.Danger,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchDistributorDetails();
    }, [store?.distributor_id]);

    // Initialise or sync parts array based on quantity_of_parts from Step 2
    useEffect(() => {
        const quantity = parseInt(store?.quantity_of_parts || '0', 10);
        if (quantity < 1) return;

        const currentParts = store?.parts || [];

        if (currentParts.length === quantity) return;

        let updatedParts: ClaimPartModule[];

        if (currentParts.length < quantity) {
            // Add new empty parts to reach the target quantity
            updatedParts = [
                ...currentParts,
                ...Array.from(
                    { length: quantity - currentParts.length },
                    () => new ClaimPartModule()
                ),
            ];
        } else {
            // Trim excess parts from the end
            updatedParts = currentParts.slice(0, quantity);
        }

        setValues({ parts: updatedParts });
    }, [store?.quantity_of_parts]);

    // Derived States
    const isPostRepair = useMemo(
        () => distributorDetails.distributor_claim_type === DISTRIBUTOR_CLAIM_TYPES.POST_REPAIR,
        [distributorDetails.distributor_claim_type]
    );

    // Render
    return (
        <Stepper.Step loading={loading}>
            {store?.parts?.map((part, index) => (
                <div key={part.key}>
                    <Card className="mb-4">
                        <CardBody>
                            <SubTitle
                                title={StringHelpers.title(
                                    `${crud?.models?.part || 'Part'} ${index + 1}`
                                )}
                            />

                            <FormInput
                                name={`${part.key}_part_number`}
                                type='text'
                                label={StringHelpers.title(
                                    fields?.part_number || 'Number'
                                )}
                                disabled={disabled}
                                value={part.part_number}
                                onChange={(value) => {
                                    if (typeof value !== 'string') return;
                                    setValues({
                                        parts: store.parts.map((p) =>
                                            p.key === part.key
                                                ? { ...p, part_number: value }
                                                : p
                                        ),
                                    });
                                }}
                                errorMessages={errors?.[`parts.${index}.part_number`]}
                            />

                            <FormInput
                                name={`${part.key}_description`}
                                type='textarea'
                                label={StringHelpers.title(
                                    fields?.description || 'Description'
                                )}
                                disabled={disabled}
                                value={part.description}
                                onChange={(value) => {
                                    if (typeof value !== 'string') return;
                                    setValues({
                                        parts: store.parts.map((p) =>
                                            p.key === part.key
                                                ? { ...p, description: value }
                                                : p
                                        ),
                                    });
                                }}
                                errorMessages={errors?.[`parts.${index}.description`]}
                                description={`${MAX_TEXT_AREA_LENGTH - (store?.parts[index]?.description?.length || 0)} characters remaining`}
                            />

                            {isPostRepair && (
                                <FormInput
                                    name={`${part.key}_replacement_dispatch_note_number`}
                                    type='text'
                                    label={StringHelpers.title(
                                        fields?.replacement_dispatch_note_number
                                            || 'Replacement Despatch Note Number'
                                    )}
                                    disabled={disabled}
                                    value={part.replacement_dispatch_note_number}
                                    onChange={(value) => {
                                        if (typeof value !== 'string') return;
                                        setValues({
                                            parts: store.parts.map((p) =>
                                                p.key === part.key
                                                    ? { ...p, replacement_dispatch_note_number: value }
                                                    : p
                                            ),
                                        });
                                    }}
                                    errorMessages={errors?.[`parts.${index}.replacement_dispatch_note_number`]}
                                />
                            )}

                            <FormInput
                                name={`${part.key}_fault_symptoms`}
                                type='textarea'
                                label={StringHelpers.title(
                                    fields?.fault_symptoms || 'Fault Symptoms'
                                )}
                                disabled={disabled}
                                value={part.fault_symptoms}
                                onChange={(value) => {
                                    if (typeof value !== 'string') return;
                                    setValues({
                                        parts: store.parts.map((p) =>
                                            p.key === part.key
                                                ? { ...p, fault_symptoms: value }
                                                : p
                                        ),
                                    });
                                }}
                                errorMessages={errors?.[`parts.${index}.fault_symptoms`]}
                                description={`${MAX_TEXT_AREA_LENGTH - (store?.parts[index]?.fault_symptoms?.length || 0)} characters remaining`}
                            />

                            <FormInput
                                name={`${part.key}_repair_details`}
                                type='textarea'
                                label={StringHelpers.title(
                                    fields?.repair_details || 'Repair Details'
                                )}
                                disabled={disabled}
                                value={part.repair_details}
                                onChange={(value) => {
                                    if (typeof value !== 'string') return;
                                    setValues({
                                        parts: store.parts.map((p) =>
                                            p.key === part.key
                                                ? { ...p, repair_details: value }
                                                : p
                                        ),
                                    });
                                }}
                                errorMessages={errors?.[`parts.${index}.repair_details`]}
                                description={`${MAX_TEXT_AREA_LENGTH - (store?.parts[index]?.repair_details?.length || 0)} characters remaining`}
                            />

                            <FormInput
                                name={`${part.key}_previously_repaired`}
                                type='checkbox'
                                label={StringHelpers.title(
                                    fields?.previously_repaired || 'Previously Replaced'
                                )}
                                disabled={disabled}
                                value={part.previously_repaired}
                                onChange={(value) => {
                                    if (typeof value !== 'boolean') return;
                                    setValues({
                                        parts: store.parts.map((p) =>
                                            p.key === part.key
                                                ? { ...p, previously_repaired: value }
                                                : p
                                        ),
                                    });
                                }}
                                errorMessages={errors?.[`parts.${index}.previously_repaired`]}
                                description={
                                    part.previously_repaired
                                        ? StringHelpers.sentence(
                                            fields?.replacing_spare_of_spare
                                                || 'Replacing a spare of a spare'
                                        )
                                        : undefined
                                }
                            />
                        </CardBody>
                    </Card>
                </div>
            ))}
        </Stepper.Step>
    );
}

export default ClaimsStep3;
